import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, Injector } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSortModule } from '@angular/material/sort';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BidiModule } from '@angular/cdk/bidi';
import { MatRippleModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSliderModule } from '@angular/material/slider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatTreeModule } from '@angular/material/tree';
import { TextFieldModule } from '@angular/cdk/text-field';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BaseComponent } from './components/base/base.component';
import { LovComponent } from './components/lov/lov.component';
import { ConfirmationDialog } from './dialogs/confirmation/confirmation-dialog';
import { ServiceLocator } from './services/locator.service';
import { TranslatePipe } from './shared/translate/translate.pipe';
import { RoutingGuardService } from './services/routing-guard.service';
import { APIInterceptor } from './shared/api-interceptor';
import { AppDateAdapter, APP_DATE_FORMATS } from './classes/AppDateAdapter';
import { HomeComponent } from './components/home/home.component';
import { ToastrModule } from 'ngx-toastr';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { SafeHtmlPipe } from './shared/safe-html.pipe';
import { SafeResourcePipe } from './shared/safe-resource.pipe';
import { DateFormatPipe } from './shared/date-format.pipe';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { LoginComponent } from './components/login/login.component';
import { CommonModule, DecimalPipe, registerLocaleData } from '@angular/common';
import { HttpClickDirective } from './directives/http-click.directive';
import { DynamicFlexDirective } from './directives/dynamic-flex.directive';
import { DirectionDirective } from './directives/direction.directive';
import { MessageBoxDirective } from './directives/message-box.directive';
import { ShuttleComponent } from './components/shuttle/shuttle.component';
import { HasRightDirective } from './directives/has-right.directive';
import { MatPaginatorIntlImpl } from './classes/MatPaginatorIntlImpl';
import { NgxMaskModule } from 'ngx-mask';
import { TableOptionsComponent } from './components/table-options/table-options.component';
import localeJOR from '@angular/common/locales/ar-JO';
import { ErrorDialog } from './dialogs/error-dialog/error-dialog';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from 'ng-pick-datetime';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { ImageViewerModule } from 'ngx-image-viewer';
import { TextBoxComponent } from './components/text-box/text-box.component';
import { ConfigurationComponent } from './components/configuration/configuration.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
registerLocaleData(localeJOR);

@NgModule({
  declarations: [
    AppComponent,
    BaseComponent,
    LovComponent,
    HomeComponent,
    SafeHtmlPipe,
    SafeResourcePipe,
    DateFormatPipe,
    ConfirmationDialog,
    TranslatePipe,
    HeaderComponent,
    FooterComponent,
    LoginComponent,
    HttpClickDirective,
    DynamicFlexDirective,
    DirectionDirective,
    MessageBoxDirective,
    ShuttleComponent,
    HasRightDirective,
    TableOptionsComponent,
    ErrorDialog,
    TextBoxComponent,
    ConfigurationComponent,
    UserProfileComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HammerModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    BidiModule,
    MatRippleModule,
    MatSelectModule,
    TextFieldModule,
    MatExpansionModule,
    MatPaginatorModule,
    MatSliderModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTabsModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatRadioModule,
    MatBadgeModule,
    MatDividerModule,
    MatDialogModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    MatChipsModule,
    MatStepperModule,
    MatTreeModule,
    FlexLayoutModule,
    LoadingBarHttpClientModule,
    MatProgressBarModule,
    NgxMaskModule.forRoot(),
    ToastrModule.forRoot({
      maxOpened: 5,
      autoDismiss: true,
      tapToDismiss: true,
      closeButton: true,
      timeOut: 5000,
      preventDuplicates: true,
      includeTitleDuplicates: true,
      resetTimeoutOnDuplicate: true,
      countDuplicates: true
    }),
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ClipboardModule,
    ImageViewerModule.forRoot()
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: APIInterceptor, multi: true },
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlImpl },
    RoutingGuardService,
    DecimalPipe,
    TranslatePipe//make it injectable
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private injector: Injector) {
    ServiceLocator.injector = this.injector;// Create global Service Injector.
  }
}
